import { useAuth } from '@/lib/hooks/useAuth'
import React, { useState } from 'react'
import * as Yup from 'yup'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { Form } from 'react-aria-components'
import { trackCtaClick, trackLoginEvent } from '@/lib/gtm/gtmV2'
import { DSTextInput } from '@/components/ds/TextInput/TextInput'
import { DSFormErrorText } from '@/components/ds/FormErrorText/FormErrorText'
import { DSLinkText } from '@/components/ds/LinkText/LinkText'
import { DSSpacer, DSTextAndIcon, DSButton, DSText } from '@zoe/ds-web'
import { DSLinkButton } from '@/components/ds/LinkButton/LinkButton'

// Create validation schema with Yup
const loginFormSchema = Yup.object({
  email: Yup.string().email('Email address does not look right').required('Email address is required'),
  password: Yup.string().required('Password is required'),
})

// TypeScript type for form values
interface LoginFormValues {
  email: string
  password: string
}

export type LoginProductType = 'daily30' | 'zoeMembership'

interface LoginFormProps {
  initialValues?: LoginFormValues
  infoText?: string
  infoVariant?: 'info' | 'welcome'
  emailInputRightContent?: React.ReactNode
  disableEmailField?: boolean
  productType?: LoginProductType
  displayForgotPassword?: boolean
}

export const NewLoginForm = ({ initialValues, displayForgotPassword = true }: LoginFormProps) => {
  const { login } = useAuth()
  const [authError, setAuthError] = useState(false)

  const defaultValues: LoginFormValues = {
    email: initialValues?.email || '',
    password: initialValues?.password || '',
  }

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isValid },
  } = useForm<LoginFormValues>({
    resolver: yupResolver(loginFormSchema),
    defaultValues,
    mode: 'onSubmit',
  })

  const onSubmit = async (values: LoginFormValues) => {
    try {
      const response = await login(values.email, values.password)
      if (response.error || !response.user) {
        setAuthError(true)
      } else {
        trackLoginEvent({ section: 'account' })
      }
    } catch {
      setAuthError(true)
    }
  }

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <DSTextInput
        name="email"
        control={control}
        label="Email address"
        placeholder="Email address"
        type="email"
        isRequired
        testId="email"
      />
      <DSSpacer size={16} direction="vertical" />

      <DSTextInput
        name="password"
        control={control}
        label="Password"
        type="password"
        placeholder="Password"
        isRequired
        testId="password"
      />
      <DSSpacer size={16} direction="vertical" />

      {authError && (
        <>
          <DSFormErrorText>
            Email or password invalid. Try again or{' '}
            <DSLinkText href="/reset-password" colourScheme="inherit">
              reset password
            </DSLinkText>
            .
          </DSFormErrorText>
          <DSSpacer size={12} direction="vertical" />
        </>
      )}

      <DSButton
        type="submit"
        isDisabled={isSubmitting || !isValid}
        variant="primary"
        size="large"
        width="full"
        testId="login-button"
        onPress={() => trackCtaClick('account', 'login-page-login-button')}
      >
        <DSTextAndIcon
          icon={{
            name: 'arrow-right',
            position: 'right',
            size: 16,
          }}
          variant="fluid-paragraph-200"
          weight="semiBold"
        >
          Login with ZOE
        </DSTextAndIcon>
      </DSButton>

      <DSSpacer size={8} direction="vertical" />

      <DSSpacer size={16} direction="vertical" />
      <DSLinkButton
        variant="tertiary"
        size="large"
        colourScheme="default"
        width="full"
        href="/reset-password"
        onPress={() => trackCtaClick('account', 'login-page-forgot-password')}
      >
        <DSText variant="fluid-paragraph-200" weight="semiBold" color="link">
          Forgot password?
        </DSText>
      </DSLinkButton>
      <DSSpacer size={16} direction="vertical" />
    </Form>
  )
}
